import React, { FC, useMemo } from 'react'
import { FormatInputValueFunction, NumberFormatProps } from 'react-number-format'
import { Form, Input as InputAnt } from 'antd'
import { i18n, TooltipLabelComponent, UICurrencySuffix } from 'LibNeo'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/es/form/Form'
import { AntValidator, Validators } from 'Modules'
import { decimalRounded, DecimalRoundedType, currencyStringToNumber } from 'Helpers'
import { FieldNames } from 'Constants/FieldNames'
import { WrapperContainer, WrapperInputNumber } from 'LibNeo/components/Basic/Input/common/styled'

type NumberFormatSelectedProps = Omit<NumberFormatProps, 'form'>
export interface CurrencyInputComponentProps extends NumberFormatSelectedProps {
	form: WrappedFormUtils
	fieldName?: string
	customValidators?: { validator: AntValidator }[]
	required?: boolean
	disabled?: boolean
	initialValue?: number | string
	onChange?: (number) => void
	placeholder?: string // placeholder или placeholderCode
	label?: string // label или labelCode
	tooltip?: string // tooltip или tooltipCode
	suffix?: UICurrencySuffix
	decimalScale?: number // допустимое кол-во знаков после запятой
	dataQa?: string
	className?: string
	tooltipCode?: string
	roundType?: DecimalRoundedType
	format?: FormatInputValueFunction
	requiredHighlight?: boolean
	fieldOptions?: GetFieldDecoratorOptions
	fixedDecimalScale?: boolean
}
export const CurrencyInput: FC<CurrencyInputComponentProps> = ({
	suffix = UICurrencySuffix.DEFAULT,
	decimalScale = 2,
	form,
	disabled,
	dataQa,
	fieldName = FieldNames.AMOUNT,
	placeholder,
	label,
	initialValue,
	required,
	onChange,
	tooltip = null,
	customValidators = [],
	roundType = DecimalRoundedType.ROUND,
	format,
	onBlur,
	onFocus,
	thousandSeparator = ' ',
	decimalSeparator = ',',
	requiredHighlight = false,
	fieldOptions,
	fixedDecimalScale,
	className,
}) => {
	const { getFieldDecorator, getFieldError } = form
	const isEmptyValue = !form?.getFieldValue(fieldName)

	const onChangeInput = (e): void => {
		const value = currencyStringToNumber(e.target.value)
		if (onChange) onChange(value)
	}

	const placeholderTranslate = useMemo(
		(): string => i18n(placeholder),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)
	const labelTranslate = useMemo(
		(): string => i18n(label),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)
	const tooltipTranslate = useMemo(
		(): string => i18n(tooltip),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const optionsField = {
		rules: [
			...customValidators,
			{
				validator: Validators.checkRequired({ required }),
			},
		],
		initialValue,
		onChange: onChangeInput,
		normalize: (value: string): number | string =>
			decimalRounded(currencyStringToNumber(value), { type: roundType }) ?? '',
	}

	const input = (
		<WrapperInputNumber
			suffix={suffix}
			hasError={getFieldError(fieldName)}
			disabled={disabled}
			hasSuffix={Boolean(suffix)}
			empty={isEmptyValue && requiredHighlight}
			thousandSeparator={thousandSeparator}
			decimalSeparator={decimalSeparator}
			decimalScale={decimalScale}
			qa-data={dataQa || `${fieldName}-qa-input`}
			className={className}
			placeholder={placeholderTranslate}
			name={fieldName}
			customInput={InputAnt}
			onFocus={onFocus}
			onBlur={onBlur}
			format={format}
			fixedDecimalScale={fixedDecimalScale}
		/>
	)
	return (
		<WrapperContainer className={className}>
			{(label || tooltip) && (
				<TooltipLabelComponent
					title={labelTranslate}
					required={required}
					tooltip={tooltipTranslate}
				/>
			)}
			<Form.Item>
				{getFieldDecorator(fieldName, { ...optionsField, ...fieldOptions })(input)}
			</Form.Item>
		</WrapperContainer>
	)
}
